import { ItemMetadata } from 'src/API';

export enum Attribute_Type {
  STANDARD = 'STANDARD',
  PRODUCT_LINE = 'PRODUCT LINE',
  PROGRAM = 'PROGRAM',
}

export enum ChangeReviewStatus {
  APPROVED = 'APPROVED',
  DENIED = 'DENIED',
  PENDING = 'PENDING',
}

export enum AdminActionStatus {
  PENDING_APPROVAL = 'Pending',
  NO_ACTION = 'No Action',
}

// Response of listAttributeDetails Query
export type AttributeDetailsResponse = {
  data: Array<AttributeDetails>;
  pagination: PaginationMetadata;
};

export type PaginationMetadata = {
  current_page: number;
  has_next: boolean;
  has_previous: boolean;
  page_size: number;
  total_count: number;
  total_pages: number;
};

export interface AttributeDetails {
  attribute_type_id: number;

  attribute_type_label: string; // Example: CATEGORY | CHANNEL | INDOOR/OUTDOOR | PC/PK COUNT
  attribute_level_label: Attribute_Type; // STANDARD | PROGRAM | PRODUCT LINE |

  attribute_type_justification?: string | null;
  attribute_type_status: ChangeReviewStatus;

  attribute_value_details: Array<AttributeValueDetails>;
  item_metadata: ItemMetadata;
}

export interface AttributeValueDetails {
  attribute_value_id: number;
  attribute_value_label: string;

  attribute_value_justification?: string | null;
  attribute_value_status: ChangeReviewStatus;

  product_line_count: number;
  rpn_count: number;
  fpn_count: number;
  item_metadata: ItemMetadata;
}

// ------------------------------------------------------------

export interface ManageAttributesTableItems extends AttributeDetails {
  admin_action_status: AdminActionStatus;
  attribute_value_labels: string;
  rpn_count: number;
  fpn_count: number;
  product_line_count: number;
}

export type AttributeDetailsInput = {
  attribute_level_label: string;
  attribute_type_id?: number | null;
  attribute_type_justification?: string | null;
  attribute_type_label: string;
  attribute_type_status: ChangeReviewStatus;
  attribute_value_details: Array<AttributeValueDetailsInput>;
  item_metadata: ItemMetadata;
};

export type AttributeValueDetailsInput = {
  attribute_value_id?: number | null;
  attribute_value_justification?: string | null;
  attribute_value_label: string;
  attribute_value_status: ChangeReviewStatus;
  item_metadata: ItemMetadata;
};

export interface AttributeState {
  masterAttributesData: AttributeDetails[];
  selectedAttribute: ManageAttributesTableItems | null;
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
  hasSuccessfulFetch: boolean;
  lastFetchTimestamp: number | null;
}

export interface ExistingAttributesTableItems {
  attributeLevelLabel: string;
  attributeTypeId: number;
  attributeTypeLabel: string;
  attributeTypeStatus: ChangeReviewStatus;
  attributeTypeJustification: string | null;
  attributeTypeItemMetadata: ItemMetadata;
  attributeValueId: number;
  attributeValueLabel: string;
  attributeValueStatus: ChangeReviewStatus;
  attributeValueJustification: string | null;
  rpn_count: number;
  fpn_count: number;
  productLines: number[];
  fpnCount: number;
  rpnCount: number;
  productLineCount: number;
  attributeValueItemMetadata?: ItemMetadata;
  requestedBy: string;
  requestedAt: string;
  reviewedBy: string;
  reviewedAt: string;
}

export interface PendingAttributesTableItems {
  attributeLevelLabel: string;
  attributeTypeId: number;
  attributeTypeLabel: string;
  attributeTypeStatus: ChangeReviewStatus;
  attributeTypeJustification: string | null;
  attributeTypeItemMetadata: ItemMetadata;
  attributeValueId: number;
  attributeValueLabel: string;
  attributeValueStatus: ChangeReviewStatus;
  attributeValueJustification: string | null;
  attributeValueItemMetadata: ItemMetadata;
  requestedBy: string;
  requestedAt: string;
}

export interface AttributeValuesForm {
  attributeValueLabel: string;
  attributeValueStatus: ChangeReviewStatus;
  attributeValueJustification: string;
}

export interface CreateAttributeFormValues {
  attributeLevelLabel: string | null;
  attributeTypeId?: number | null;
  attributeTypeLabel: string | null;
  attributeTypeStatus: ChangeReviewStatus;
  attributeTypeJustification: string | null;
  attributeTypeItemMetadata?: ItemMetadata | null;
  attributeValues: AttributeValuesForm[];
}

export interface CreateAttributeFormState {
  values: CreateAttributeFormValues;
  dirty: boolean;
  isValid: boolean;
  isSubmitting: boolean;
  isValidating: boolean;
}

// Ticketing types for attribute approval ticket creation
export interface CreateAttributeApprovalTicketInput {
  attribute_type: string;
  attribute_values: string[];
  attribute_level: string;
  user_alias: string;
  justifications: string[];
}

export interface TicketCreationResponse {
  ticket_id: string;
}

// ============================================================================
// AI-assisted attribute/value matching (Option A: client-side)
// ============================================================================

/**
 * What kind of thing the user is asking to create — a brand-new attribute
 * (type) or a new value under an existing/new attribute.
 */
export enum AttributeMatchTargetKind {
  ATTRIBUTE_TYPE = 'ATTRIBUTE_TYPE',
  ATTRIBUTE_VALUE = 'ATTRIBUTE_VALUE',
}

/**
 * A single existing attribute or value that the matcher believes is
 * semantically close to what the user asked for.
 */
export interface AttributeMatchCandidate {
  kind: AttributeMatchTargetKind;
  /** The requested text the user typed (e.g. "SUPPLIER"). */
  requestedLabel: string;
  /** The existing label that matched (e.g. "VENDOR"). */
  existingLabel: string;
  /** Confidence in the range [0, 1]; higher is a closer match. */
  score: number;
  /** Human-readable explanation of why this matched (for the modal). */
  reason: string;
  /**
   * True when the match is under the SAME attribute the user is editing
   * (in-place reuse). False/undefined for cross-attribute matches, which are
   * reused by navigating to the attribute the value lives under.
   */
  sameAttribute?: boolean;
  /** The existing attribute this candidate belongs to (always set). */
  attribute: AttributeDetails;
  /**
   * For ATTRIBUTE_VALUE matches, the existing value that matched. Undefined
   * for ATTRIBUTE_TYPE matches.
   */
  attributeValue?: AttributeValueDetails;
}

/**
 * The matcher's verdict for a single requested item (one attribute name or
 * one value name).
 */
export interface AttributeMatchResult {
  kind: AttributeMatchTargetKind;
  requestedLabel: string;
  /**
   * Blocking candidates — matches that should stop a silent auto-create and
   * prompt the user to reuse. For values these are same-attribute matches; for
   * attribute types these are all matches. Sorted by descending score.
   */
  candidates: AttributeMatchCandidate[];
  /**
   * Non-blocking, informational candidates — matches that exist under a
   * DIFFERENT attribute. Surfaced as an FYI ("this value also exists under X")
   * but the user is free to create the new value. Sorted by descending score.
   */
  informationalCandidates: AttributeMatchCandidate[];
  /** True when at least one blocking candidate cleared the threshold. */
  hasMatch: boolean;
  /** True when at least one informational (cross-attribute) candidate exists. */
  hasInformational: boolean;
}

/**
 * Aggregate outcome for an entire form submission. Collects a per-item result
 * for the attribute name and each requested value.
 */
export interface AttributeMatchAssessment {
  results: AttributeMatchResult[];
  /** True when any result has a BLOCKING match (gates auto-create). */
  hasAnyMatch: boolean;
  /** True when any result has a non-blocking informational match. */
  hasAnyInformational: boolean;
  /**
   * True when the modal should be shown at all — i.e. there is either a
   * blocking match or something informational worth surfacing.
   */
  shouldPrompt: boolean;
}

/**
 * Pluggable matching contract. The Option A implementation is fully
 * client-side; a future Bedrock-backed implementation can satisfy the same
 * interface (returning a Promise) without changing the UI.
 */
export interface AttributeMatcher {
  assess(
    formValues: CreateAttributeFormValues,
    existingAttributes: AttributeDetails[]
  ): AttributeMatchAssessment | Promise<AttributeMatchAssessment>;
}
